import { useState, useEffect } from 'react';

import { getOrderVelocity } from '../../../store/inventory/inventory.api';
import { TimePeriod, DEFAULT_LOOKBACK_PERIOD } from '../utils/time-periods';

export const VelocityErrors = {
  NO_DATA: 'NO_DATA',
  GENERIC_ERROR: 'GENERIC_ERROR',
};

/**
 * Hook to fetch and manage order velocity data
 * @param {Array} inventoryEntities - Array of inventory entities
 * @param {string} lookbackPeriod - The lookback period to use
 * @returns {{
 *   orderVelocityData: {data: Array, missingSkus?: Array} | null,
 *   apiLoading: boolean,
 *   error: null | 'NO_DATA' | 'GENERIC_ERROR'
 * }} The order velocity data, loading state, and error type
 */
export const useOrderVelocity = (inventoryEntities, lookbackPeriod) => {
  const [orderVelocityData, setOrderVelocityData] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [errorType, setErrorType] = useState(null);

  // Fetch order velocity data when inventory data is available or when refresh is needed
  useEffect(() => {
    if (inventoryEntities.length > 0 && !apiLoading && !orderVelocityData) {
      setApiLoading(true);

      const skuIds = Array.from(new Set(inventoryEntities.filter((entity) => entity.sku).map((entity) => entity.sku)));

      const lookbackMonths = TimePeriod[lookbackPeriod]?.months ?? TimePeriod[DEFAULT_LOOKBACK_PERIOD].months;

      if (skuIds.length > 0) {
        const orderVelocityPromise = getOrderVelocity({
          skuIds,
          lookbackMonths,
        });

        orderVelocityPromise.promise
          .then((response) => {
            if (response == null) {
              setErrorType(VelocityErrors.NO_DATA);
              setOrderVelocityData({ data: null });
            } else {
              setErrorType(null);
              setOrderVelocityData({
                data: response.data,
                missingSkus: response.meta?.missingSkus ?? [],
              });
            }
          })
          .catch(() => {
            setErrorType(VelocityErrors.GENERIC_ERROR);
            setOrderVelocityData({ data: null });
          })
          .finally(() => {
            setApiLoading(false);
          });
      } else {
        // No SKUs to process
        setOrderVelocityData({ data: null });
        setApiLoading(false);
      }
    }
  }, [inventoryEntities, apiLoading, lookbackPeriod, orderVelocityData]);

  return {
    orderVelocityData,
    apiLoading,
    error: errorType,
  };
};

export default useOrderVelocity;
